import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessibilityTag } from '../../config'
import { IconButton, IconButtonSize, IconButtonVariants } from '../button'
import { ChevronDownIcon, ChevronUpIcon } from '../icon'
import styles from './Expander.module.scss'
import type { ExpanderProps } from './types'

const Expander: React.FC<ExpanderProps> = ({
  ariaLabelExpandButton,
  ariaLabelCollapseButton,
  children,
  className,
  disabled = false,
  divider = true,
  expanded = false,
  loading = false,
  onClick,
  auto = false,
}) => {
  const [open, setOpen] = useState(expanded)
  const expanderRef = useRef<HTMLDivElement>(null)
  const ariaLabelExpand = getAccessibilityTag('expand')
  const ariaLabelCollapse = getAccessibilityTag('collapse')

  const handleClick = useCallback(() => {
    if (disabled) return
    if (auto) {
      setOpen((prev) => !prev)
      return
    }
    onClick && onClick()
  }, [auto, disabled, onClick])

  useEffect(() => {
    const childHeader = expanderRef.current?.querySelector('[class*="pintxo-expander-header"]')
    if (childHeader) {
      childHeader.addEventListener('click', handleClick)
    }
    return () => {
      if (childHeader) {
        childHeader.removeEventListener('click', handleClick)
      }
    }
  }, [expanded, disabled, handleClick])

  const isExpanded = auto ? open : expanded

  return (
    <div
      className={classNames(styles['pintxo-expander'], className)}
      data-expanded={isExpanded}
      data-disabled={disabled}
      ref={expanderRef}
    >
      <div className={styles['pintxo-expander__icon-button']}>
        <IconButton
          aria-label={
            isExpanded
              ? ariaLabelCollapseButton || ariaLabelCollapse
              : ariaLabelExpandButton || ariaLabelExpand
          }
          aria-expanded={isExpanded}
          disabled={disabled}
          icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
          loading={loading}
          onClick={handleClick}
          size={IconButtonSize.small}
          variant={IconButtonVariants.transparent}
        />
      </div>
      {divider && <div className={styles['pintxo-expander__divider']} />}
      {children}
    </div>
  )
}

export default Expander
